import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";
import { Loader2 } from "lucide-react"

import { cn } from "@/lib/utils"

/* Canonical action primitive. One blue, flat, rounded-lg (radius token), fast press.
   Variants: default · soft · soft-primary · outline · secondary · ghost · destructive · link
   Shape:    rounded="lg" (default) | "full" (pill — hero/CTA/mobile sticky actions) */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium select-none " +
  "transition-[background-color,color,opacity,transform,box-shadow] duration-fast ease-standard " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background " +
  "disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98] " +
  "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary-hover",
        destructive: "bg-danger text-white hover:bg-red-700",
        outline: "border border-input bg-surface text-foreground hover:bg-surface-subtle",
        secondary: "bg-secondary text-secondary-foreground hover:bg-gray-200",
        soft: "bg-gray-100 text-gray-700 hover:bg-gray-200",
        "soft-primary": "bg-primary-soft text-primary-soft-foreground hover:bg-blue-100",
        "soft-danger": "bg-danger-soft text-danger-soft-foreground hover:bg-red-100",
        ghost: "text-gray-700 hover:bg-gray-100 hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        xs: "h-7 px-2.5 text-xs",
        sm: "h-8 px-3 text-xs",
        default: "h-9 px-4 py-2",
        lg: "h-11 px-6 text-base",
        icon: "h-9 w-9",
        "icon-sm": "h-8 w-8",
        "icon-lg": "h-11 w-11",
      },
      rounded: {
        lg: "rounded-lg",
        full: "rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      rounded: "lg",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, rounded, asChild = false, loading = false, disabled, children, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, rounded, className }))}
      ref={ref}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}>
      {loading && !asChild ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
      {children}
    </Comp>
  );
})
Button.displayName = "Button"

/* Square/round icon-only button. Always pass aria-label. Defaults to the
   gray-100 pill used in the Manifest header (the canonical icon-button look). */
const IconButton = React.forwardRef(({ className, variant = "soft", size = "icon", rounded = "full", ...props }, ref) => (
  <Button ref={ref} variant={variant} size={size} rounded={rounded} className={cn("shrink-0", className)} {...props} />
))
IconButton.displayName = "IconButton"

export { Button, IconButton, buttonVariants }
